import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
    Mail, 
    Send, 
    Loader2, 
    CheckCircle, 
    ArrowLeft,
    User,
    MessageSquare,
    FileText
} from 'lucide-react';
import { toast } from 'sonner';

const ContactPage = () => {
    const { user } = useAuth();
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [formData, setFormData] = useState({
        name: user?.name || '',
        email: user?.email || '',
        subject: '',
        message: ''
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.email || !formData.subject || !formData.message) {
            toast.error('Veuillez remplir tous les champs');
            return;
        }

        setLoading(true);
        try {
            await api.sendContactMessage(formData);
            setSent(true);
            toast.success('Message envoyé avec succès!');
        } catch (error) {
            console.error('Error sending message:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi du message');
        } finally {
            setLoading(false);
        }
    };

    if (sent) {
        return (
            <div className="min-h-screen bg-background py-12 px-4">
                <div className="max-w-lg mx-auto">
                    <div className="bg-card rounded-3xl border-2 border-green-400 shadow-[6px_6px_0px_0px_#22c55e] p-8 text-center">
                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle className="w-10 h-10 text-green-600" />
                        </div>
                        <h1 className="font-heading text-3xl font-black text-foreground mb-4">
                            Message envoyé!
                        </h1>
                        <p className="text-muted-foreground mb-6">
                            Merci de nous avoir contactés. Nous vous répondrons dans les plus brefs délais.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                            <Button
                                onClick={() => {
                                    setSent(false);
                                    setFormData(prev => ({ ...prev, subject: '', message: '' }));
                                }}
                                variant="outline"
                                className="border-2 border-foreground"
                            >
                                Envoyer un autre message
                            </Button>
                            <Link to="/">
                                <Button className="bg-primary text-white hover:bg-primary/90 w-full sm:w-auto">
                                    Retour à l'accueil
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background py-12 px-4">
            <div className="max-w-2xl mx-auto">
                {/* Back link */}
                <Link 
                    to="/" 
                    className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Retour à l'accueil
                </Link>

                {/* Header */}
                <div className="text-center mb-10">
                    <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-primary">
                        <Mail className="w-10 h-10 text-primary" />
                    </div>
                    <h1 className="font-heading text-4xl md:text-5xl font-black text-foreground mb-4">
                        Contactez-nous
                    </h1>
                    <p className="text-muted-foreground text-lg max-w-md mx-auto">
                        Une question, une suggestion ou un commentaire? N'hésitez pas à nous écrire!
                    </p>
                </div>

                {/* Contact Form */}
                <div className="bg-card rounded-3xl border-2 border-foreground shadow-pop p-6 md:p-10">
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Name */}
                        <div className="space-y-2">
                            <Label htmlFor="name" className="flex items-center gap-2 font-bold">
                                <User className="w-4 h-4 text-primary" />
                                Votre nom
                            </Label>
                            <Input
                                id="name"
                                type="text"
                                placeholder="Ex: Marie Tremblay"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                className="border-2 border-foreground/20 focus:border-primary"
                                required
                            />
                        </div>

                        {/* Email */}
                        <div className="space-y-2">
                            <Label htmlFor="email" className="flex items-center gap-2 font-bold">
                                <Mail className="w-4 h-4 text-primary" />
                                Votre email
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="Ex: marie@example.com"
                                value={formData.email}
                                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                className="border-2 border-foreground/20 focus:border-primary"
                                required
                            />
                        </div>

                        {/* Subject */}
                        <div className="space-y-2">
                            <Label htmlFor="subject" className="flex items-center gap-2 font-bold">
                                <FileText className="w-4 h-4 text-primary" />
                                Sujet
                            </Label>
                            <Input
                                id="subject"
                                type="text"
                                placeholder="Ex: Question sur les recettes"
                                value={formData.subject}
                                onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                                className="border-2 border-foreground/20 focus:border-primary"
                                required
                            />
                        </div>

                        {/* Message */}
                        <div className="space-y-2">
                            <Label htmlFor="message" className="flex items-center gap-2 font-bold">
                                <MessageSquare className="w-4 h-4 text-primary" />
                                Votre message
                            </Label>
                            <textarea
                                id="message"
                                placeholder="Écrivez votre message ici..."
                                value={formData.message}
                                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                                className="w-full min-h-[150px] p-3 border-2 border-foreground/20 focus:border-primary rounded-lg resize-y"
                                required
                            />
                        </div>

                        {/* Submit Button */}
                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-primary text-white hover:bg-primary/90 py-6 text-lg font-bold border-2 border-foreground shadow-pop"
                            data-testid="contact-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                    Envoi en cours...
                                </>
                            ) : (
                                <>
                                    <Send className="w-5 h-5 mr-2" />
                                    Envoyer le message
                                </>
                            )}
                        </Button>
                    </form>
                </div>

                {/* Info box */}
                <div className="mt-8 bg-blue-50 rounded-2xl p-6 border-2 border-blue-200">
                    <p className="text-blue-700 text-center">
                        📧 Nous répondons généralement dans un délai de 24 à 48 heures.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default ContactPage;
